import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Icon, SearchableSelect, SidePanel } from '../ui';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import type { CommsBoard } from '../../lib/comms';
import { formatDate } from '../../utils/dateUtils';
import {
  invokeTrelloBoard, labelColor,
  type TrelloCard, type TrelloList,
} from '../../lib/trelloBoard';
import TrelloCardDetailBody from './TrelloCardDetailBody';

interface BoardExplorerPanelProps {
  open: boolean;
  onClose: () => void;
  /** Active comms boards; the picker lists these. */
  boards: CommsBoard[];
  /** Card ids already on the Open/Assigned tables — shown as such, not importable. */
  presentCardIds: Set<string>;
  /** Called after a successful import so the page can refetch items. */
  onImported: () => void;
}

/**
 * Trello board explorer for Client Comms. Read-only view of a board's open
 * lists and cards, with a manual "Import" that upserts one comms_items row
 * (kind manual_import, trello_action_id manual-<cardId>) so the card lands
 * on the Open table without waiting for client activity.
 */
export default function BoardExplorerPanel({
  open,
  onClose,
  boards,
  presentCardIds,
  onImported,
}: BoardExplorerPanelProps) {
  const { user } = useUser();
  const [boardId, setBoardId] = useState<string>('');
  const [lists, setLists] = useState<TrelloList[]>([]);
  const [cards, setCards] = useState<TrelloCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());
  // Card opened in the layered detail panel (list name rides along for the eyebrow/import)
  const [openCard, setOpenCard] = useState<{ card: TrelloCard; listName: string } | null>(null);

  const board = boards.find((b) => b.id === boardId) ?? null;

  useEffect(() => {
    if (!open) {
      setOpenCard(null);
      return;
    }
    setImportedIds(new Set());
    setBoardId((prev) => (prev && boards.some((b) => b.id === prev) ? prev : boards[0]?.id ?? ''));
  }, [open, boards]);

  useEffect(() => {
    if (!open || !board) {
      setLists([]);
      setCards([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOpenCard(null);
    invokeTrelloBoard<{ lists: TrelloList[]; cards: TrelloCard[] }>({
      action: 'board',
      boardId: board.trello_board_id,
    })
      .then((data) => {
        if (cancelled) return;
        setLists((data.lists || []).slice().sort((a, b) => a.pos - b.pos));
        setCards(data.cards || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading Trello board:', err);
        setError('Failed to load the Trello board');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, board?.trello_board_id]);

  const cardsByList = useMemo(() => {
    const grouped = new Map<string, TrelloCard[]>();
    for (const card of cards) {
      const bucket = grouped.get(card.idList);
      if (bucket) bucket.push(card);
      else grouped.set(card.idList, [card]);
    }
    for (const bucket of grouped.values()) bucket.sort((a, b) => a.pos - b.pos);
    return grouped;
  }, [cards]);

  const handleImport = async (card: TrelloCard, listName: string) => {
    if (!board || importingId) return;
    setImportingId(card.id);
    setError(null);
    const { error: importError } = await supabase.from('comms_items').upsert(
      {
        trello_action_id: `manual-${card.id}`,
        board_id: board.id,
        card_id: card.id,
        card_name: card.name,
        card_url: card.shortUrl,
        card_list_name: listName,
        kind: 'manual_import',
        actor_name: user?.name || 'Manual import',
        actor_trello_id: 'manual',
        body: null,
        occurred_at: new Date().toISOString(),
        status: 'open',
        cleared_by: null,
        cleared_at: null,
        cleared_via: null,
        direction: 'inbound',
      },
      { onConflict: 'trello_action_id' }
    );
    setImportingId(null);
    if (importError) {
      console.error('Error importing card to comms:', importError);
      setError('Import failed — check the console.');
      return;
    }
    setImportedIds((prev) => new Set(prev).add(card.id));
    onImported();
  };

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        eyebrow="Client Comms"
        title="Board Explorer"
        width="100vw"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
            <SearchableSelect
              label="Board"
              placeholder="Pick a board..."
              searchPlaceholder="Search boards..."
              options={boards.map((b) => ({ value: b.id, label: b.board_name }))}
              value={boardId || null}
              onChange={(v) => setBoardId(v ?? '')}
              style={{ minWidth: '280px' }}
            />
            {board ? (
              <a
                href={`https://trello.com/b/${board.trello_board_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex' }}
              >
                <Button size="sm" variant="ghost" icon="external-link">
                  Open in Trello
                </Button>
              </a>
            ) : null}
          </div>

          {error ? (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          ) : null}

          {boards.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              No active boards. Boards appear after the next Trello sync.
            </div>
          ) : loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <div
                className="animate-spin w-7 h-7 border-2 rounded-full"
                style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
              />
            </div>
          ) : lists.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              This board has no open lists.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '6px', flex: 1 }}>
              {lists.map((list) => {
                const listCards = cardsByList.get(list.id) || [];
                return (
                  <div
                    key={list.id}
                    style={{
                      width: '236px',
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--border-hairline)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginBottom: '10px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                          color: 'var(--text-body)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {list.name}
                      </span>
                      <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                        {listCards.length}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {listCards.map((card) => {
                        const onTable = presentCardIds.has(card.id) || importedIds.has(card.id);
                        return (
                          <div
                            key={card.id}
                            style={{
                              background: 'var(--surface-card)',
                              border: '1px solid var(--border-hairline)',
                              borderRadius: 'var(--radius-md)',
                              padding: '9px 10px',
                            }}
                          >
                            {/* Labels + name open the card detail; the row below keeps its own controls */}
                            <button
                              type="button"
                              className="hover-wash pressable"
                              title="View card details"
                              onClick={() => setOpenCard({ card, listName: list.name })}
                              style={{
                                display: 'block',
                                width: '100%',
                                margin: '-4px -5px',
                                padding: '4px 5px',
                                border: 'none',
                                background: 'transparent',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'var(--font-ui)',
                                color: 'var(--text-body)',
                              }}
                            >
                              {card.labels.length > 0 && (
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                  {card.labels.map((label) => (
                                    <span
                                      key={label.id}
                                      title={label.name || undefined}
                                      style={{
                                        width: '24px',
                                        height: '5px',
                                        borderRadius: '3px',
                                        background: labelColor(label.color),
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                              <div style={{ fontSize: 'var(--text-sm)', lineHeight: 1.35 }}>
                                {card.name}
                              </div>
                            </button>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                                marginTop: '8px',
                              }}
                            >
                              <a
                                href={card.shortUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open in Trello"
                                style={{ display: 'inline-flex', color: 'var(--text-faint)' }}
                              >
                                <Icon name="external-link" size={12} />
                              </a>
                              {onTable ? (
                                <Badge tone="up" dot>In comms</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={importingId !== null}
                                  onClick={() => handleImport(card, list.name)}
                                >
                                  {importingId === card.id ? 'Importing…' : 'Import'}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SidePanel>

      {/* Card detail — opens over the explorer with the card's live Trello state */}
      <SidePanel
        open={openCard !== null}
        onClose={() => setOpenCard(null)}
        eyebrow={openCard ? `${board?.board_name ?? 'Trello'} · ${openCard.listName}` : undefined}
        title={openCard?.card.name}
        width={480}
        layer={1}
        footer={
          openCard ? (
            <span style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
              <a href={openCard.card.shortUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="secondary" icon="external-link">
                  Open in Trello
                </Button>
              </a>
              {presentCardIds.has(openCard.card.id) || importedIds.has(openCard.card.id) ? (
                <Badge tone="up" dot>In comms</Badge>
              ) : (
                <Button
                  size="sm"
                  disabled={importingId !== null}
                  onClick={() => handleImport(openCard.card, openCard.listName)}
                >
                  {importingId === openCard.card.id ? 'Importing…' : 'Import'}
                </Button>
              )}
            </span>
          ) : undefined
        }
      >
        {openCard && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {(openCard.card.labels.length > 0 || openCard.card.due) && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {openCard.card.labels.map((label) => (
                  <span
                    key={label.id}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: labelColor(label.color),
                      color: '#fff',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                    }}
                  >
                    {label.name || label.color}
                  </span>
                ))}
                {openCard.card.due && (
                  <Badge tone={openCard.card.dueComplete ? 'up' : 'warn'} dot>
                    Due {formatDate(openCard.card.due, 'MMM d, yyyy')}
                  </Badge>
                )}
              </div>
            )}
            <TrelloCardDetailBody cardId={openCard.card.id} />
          </div>
        )}
      </SidePanel>
    </>
  );
}
